/* eslint-disable complexity */
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useContext
} from 'react';
import clsx from 'clsx';
import { Container, Grid, useMediaQuery } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Title from '../../../../Components/Labels/Title';
import SimpleDivider from '../../../../Components/Dividers/SimpleDivider';
import TextInput from '../../../../Components/Inputs/TextInput';
import SelectInput from '../../../../Components/Inputs/SelectInput';
import Recaptcha from '../../../../Components/Captcha/TurnstileManagedCaptcha';
import BaseButton from '../../../../Components/Buttons/BaseButton';
import ModalProgress from '../../../../Components/Progress/Modal/ModalProgress';

import NewPaymentCard from '../../../../Components/Cards/NewPaymentCard/NewPaymentCard';

import {
  PAYMENT_METHOD,
  PAYMENT_VALUE_OPTION,
  SELECT_OPTIONS,
  TYPE_OF_PAYMENTS
} from '../../payment_enums';
import { AlertsDispatchContext } from '../../../../Contexts/AlertsContext';

// * API
import { GetCouponAPI } from '../../../../API/Coupons/CouponsAPI';
import { GetInvoicesAPI } from '../../../../API/Invoices/InvoicesAPI';
import { defaultAlertError } from '../../../../Components/Alerts/AlertMessages';
import QuestionMark from '../../../../Components/Adornments/QuestionMark';
import InvoiceHelpDialog from '../../../../Components/Dialogs/InvoiceHelpDialog/InvoiceHelpDialog';

// * Utils
import { HELP_TEXT, COUPON_VALUES } from '../../../../Utils/enums';

import { Company } from '../../../../Configs/general';
import { ValidatePartialPaymentAPI } from '../../../../API/Debts/DebtsAPI';
import SweetAlert from '../../../../Components/Alerts/SweetAlert';
import { ALERT_TYPE } from '../../../../Components/Alerts/alert_enums';
import { RecaptchaAction } from '../../../../Enums/recaptcha';
import TurnstileInvisibleCaptcha from '../../../../Components/Captcha/TurnstileInvisibleCaptcha';
import { getCouponValue } from '../../payment_enums';
import {
  isPaymentOptionDisabled,
  normalizePaymentType
} from '../../helpers/helpers';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCaptcha } from '../../../../Hooks/useCaptcha';
import { useCaptchaWithFallback } from '../../../../Hooks/useCaptchaWithFallback';

const paymentReferenceSchema = yup.object({
  number: yup
    .string()
    .required(
      `Ingrese el número de ${Company.contractConjugation.regular.singular.main}`
    )
});

const contractSingularDemostrative =
  Company.contractConjugation.capitalized.singular.demonstrative;

const IndividualPaymentGDC = props => {
  const {
    number,
    setDisabledNext,
    setNumber,
    type,
    setType,
    payment,
    setPayment,
    canPayPartially,
    setPaymentValueOption,
    setMethodSelected,
    currentUser
  } = props;

  const setAlert = useContext(AlertsDispatchContext);

  // * STATE HOOKS;
  const [loading, setLoading] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const inputRef = useRef(null);

  // * OTHER HOOKS
  const classes = useStyles();
  const isWebSize = useMediaQuery(theme => theme.breakpoints.up('sm'));
  const {
    captchaRef: visibleCaptchaRef,
    handlers: visibleCaptchaHandlers,
    captchaToken: visibleCaptchaToken,
    loading: visibleCaptchaLoading
  } = useCaptcha();

  const {
    captchaRef: invisibleCaptchaRef,
    handlers: invisibleCaptchaHandlers,
    getTokenWithFallback
  } = useCaptchaWithFallback();

  const selectedPaymentOption = SELECT_OPTIONS.find(
    option => option.value === type
  );

  const isSelectedPaymentOptionDisabled =
    selectedPaymentOption && isPaymentOptionDisabled(selectedPaymentOption);

  const canSubmit =
    !loading &&
    visibleCaptchaToken &&
    number &&
    type &&
    !isSelectedPaymentOptionDisabled;

  const { watch, register, setValue, control, handleSubmit, errors } = useForm({
    resolver: yupResolver(paymentReferenceSchema),
    defaultValues: { number }
  });

  const paymentNumber = watch('number');

  const disabledPaymentOption = SELECT_OPTIONS.find(option =>
    isPaymentOptionDisabled(option)
  );

  const hasEnabledPaymentOption = SELECT_OPTIONS.some(
    option => !isPaymentOptionDisabled(option)
  );

  const disabledPaymentOptionLabel =
    disabledPaymentOption && disabledPaymentOption.label
      ? disabledPaymentOption.label.toLowerCase()
      : '';

  useEffect(() => {
    const formIsEmpty = !paymentNumber || loading || !payment || !type;

    // Facturas pendientes con Breb pueden continuar para pagar con otro medio
    const isBrebPaymentMethodPending =
      payment &&
      payment.detail &&
      payment.detail.paymentMethod === PAYMENT_METHOD.BREB &&
      payment.detail.isPending &&
      !payment.detail.isPaid;

    const isBlockedByPending =
      payment && payment.detail.isPending && !isBrebPaymentMethodPending;

    if (formIsEmpty || payment.detail.isPaid || isBlockedByPending) {
      setDisabledNext(true);
    } else {
      // PayU does not allow us to pay coupons with such a small value (1 COP),
      // so disable the Next button when that happens.
      const value = getCouponValue(payment);

      const canPay = value >= COUPON_VALUES.MIN_VALUE;
      setDisabledNext(!canPay);
      if (!canPay) {
        setAlert({
          type: 'info',
          message: `Solo se permite realizar pagos mayores a ${COUPON_VALUES.MIN_VALUE} peso.`
        });
      }
    }
  }, [type, paymentNumber, setDisabledNext, loading, payment, setAlert]);

  const updateType = useCallback(
    nextType => {
      setType(nextType);
      setValue('number', null);
      setNumber('');
      setPayment([]);
    },
    [setType, setNumber, setPayment, setValue]
  );

  const handleType = useCallback(
    e => {
      updateType(e.target.value);
    },
    [updateType]
  );

  useEffect(() => {
    const nextType = normalizePaymentType(SELECT_OPTIONS, type);

    if (nextType === type) {
      return;
    }

    updateType(nextType);
  }, [type, updateType]);

  useEffect(() => {
    setPayment([]);
    setNumber(paymentNumber);
  }, [paymentNumber, setNumber, setPayment]);

  const onHelpClick = useCallback(() => {
    setHelpDialogOpen(true);
  }, []);

  const isBrebPaymentMethodPending =
    payment &&
    payment.detail &&
    payment.detail.paymentMethod === PAYMENT_METHOD.BREB &&
    payment.detail.isPending &&
    !payment.detail.isPaid;

  const onSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    // set states
    setLoading(true);
    setPayment([]);
    setMethodSelected('');
    setPaymentValueOption('');

    setDisabledNext(true);

    const captchaValue = visibleCaptchaToken;
    visibleCaptchaHandlers.reset();

    let paymentData;

    // api fetch
    if (type === TYPE_OF_PAYMENTS.coupon) {
      const { success, data, error } = await GetCouponAPI(number, captchaValue);
      if (success) {
        const couponData = data.data;
        paymentData = [
          {
            type: TYPE_OF_PAYMENTS.coupon,
            detail: {
              ...couponData,
              couponId: couponData.id,
              couponValue: couponData.value
            }
          }
        ];
      } else {
        defaultAlertError(error, setAlert, 'couponId');
        setLoading(false);
        return;
      }
    } else if (type === TYPE_OF_PAYMENTS.contract) {
      const { success, data, error } = await GetInvoicesAPI(
        number,
        captchaValue
      );

      if (success) {
        paymentData = [
          { type: TYPE_OF_PAYMENTS.contract, detail: data.data[0] }
        ];
      } else {
        defaultAlertError(error, setAlert);
        setLoading(false);
        return;
      }
    }

    const { couponId, contractId } = paymentData[0].detail;

    const userToken = currentUser ? currentUser.token : null;

    let recaptchaToken;
    if (!userToken) {
      recaptchaToken = await getTokenWithFallback();
    }

    const validateResponse = await ValidatePartialPaymentAPI(
      contractId,
      couponId,
      userToken,
      recaptchaToken
    );

    if (validateResponse.success) {
      const {
        valid,
        canPayPartially: canPayPartial,
        isPartialCoupon
      } = validateResponse.data.data;

      paymentData = [
        {
          ...paymentData[0],
          canBePaidPartially: canPayPartial && valid,
          isPartialCoupon
        }
      ];
    } else {
      paymentData = [{ ...paymentData[0], canBePaidPartially: false }];
    }

    setPaymentValueOption(
      paymentData[0].canBePaidPartially ? '' : PAYMENT_VALUE_OPTION.TotalCoupon
    );

    setPayment(paymentData);

    setLoading(false);
  };

  useEffect(() => {
    window.scrollTo(0, document.body.scrollHeight);
  }, []);

  return (
    <>
      <TurnstileInvisibleCaptcha
        captchaRef={invisibleCaptchaRef}
        onResolved={invisibleCaptchaHandlers.onResolved}
        onExpired={invisibleCaptchaHandlers.onExpired}
        onError={invisibleCaptchaHandlers.onError}
        onLoaded={invisibleCaptchaHandlers.onLoaded}
        action={RecaptchaAction.ValidatePartialPayments}
      />
      <InvoiceHelpDialog
        open={helpDialogOpen}
        type={type}
        onClose={() => setHelpDialogOpen(false)}
        title={HELP_TEXT[type]}
      />
      {visibleCaptchaLoading && (
        <ModalProgress
          id="QueryPayment_progress_loadingCaptcha"
          message="Cargando"
        />
      )}
      <Container
        maxWidth="md"
        className={`${classes.root} ${
          visibleCaptchaLoading ? classes.captchaLoading : ''
        }`}
      >
        {loading && (
          <ModalProgress
            id="QueryPayment_progress_loading"
            message="Buscando"
          />
        )}

        {disabledPaymentOption && (
          <Grid item xs={12} className={classes.paymentReferenceSweetAlert}>
            <SweetAlert
              type={ALERT_TYPE.WARNING}
              message={
                hasEnabledPaymentOption
                  ? `La opción de pago por ${disabledPaymentOptionLabel} no está disponible temporalmente debido a labores de mantenimiento en nuestra plataforma. Puede realizar su pago utilizando otra referencia disponible.`
                  : 'En este momento no hay referencias de pago disponibles debido a labores de mantenimiento en nuestra plataforma. Por favor intenta nuevamente más tarde.'
              }
            />
          </Grid>
        )}

        <Grid container spacing={3} className={classes.formContainer}>
          <Grid item md={4} sm={4} xs={12} className={classes.inputContainer}>
            <SelectInput
              id="QueryPayment_select_paymentMethod"
              value={type}
              onChange={handleType}
              disabled={loading}
              label="Referencia"
              fullWidth
              options={SELECT_OPTIONS}
              getOptionDisabled={isPaymentOptionDisabled}
            />
          </Grid>
          <Grid item md={4} sm={4} xs={12} className={classes.inputContainer}>
            <TextInput
              id="number"
              inputRef={register}
              control={control}
              name="number"
              autoFocus
              disabled={loading}
              error={Boolean(errors.number)}
              label={`Número ${
                type === TYPE_OF_PAYMENTS.contract
                  ? `de ${Company.contractConjugation.regular.singular.main}`
                  : 'del cupón'
              }`}
              InputProps={{
                endAdornment: <QuestionMark onClick={onHelpClick} />,
                inputProps: {
                  type: 'number',
                  inputMode: 'numeric'
                }
              }}
              required={true}
              helperText={
                errors && errors.number
                  ? errors.number.message || errors.number.types.message
                  : ''
              }
              fullWidth
            />
          </Grid>
          <Grid
            item
            sm={4}
            xs={12}
            ref={inputRef}
            className={clsx([classes.inputContainer])}
          >
            <Recaptcha
              captchaRef={visibleCaptchaRef}
              referenceEl={inputRef}
              locale="es"
              heightScale={0.9}
              onResolved={visibleCaptchaHandlers.onResolved}
              onExpired={visibleCaptchaHandlers.onExpired}
              onError={visibleCaptchaHandlers.onError}
              onLoaded={visibleCaptchaHandlers.onLoaded}
            />
          </Grid>
          <Grid item xs={12} className={classes.consultContainer}>
            <BaseButton
              id="QueryPayment_button_submit"
              onClick={handleSubmit(onSubmit)}
              disabled={!canSubmit}
              fullWidth={!isWebSize}
              color="primary"
              variant="outlined"
            >
              Consultar
            </BaseButton>
          </Grid>
        </Grid>
        <SimpleDivider withoutMargin />
        {payment && (
          <div style={{ marginBottom: '80px' }}>
            {isBrebPaymentMethodPending && (
              <SweetAlert
                id="AuthHome_div_isBrebPaymentMethodPending_info"
                classes={{ root: classes.sweetAlert }}
                type={ALERT_TYPE.WARNING}
                message={
                  <>
                    Actualmente tienes un proceso de pago activo con Bre-B.
                    Puedes pagar con el QR generado o utilizar otro medio de
                    pago con tu número de cupón.
                  </>
                }
              />
            )}
            <Title
              text={`Detalle ${
                payment.type === TYPE_OF_PAYMENTS.coupon
                  ? 'del cupón'
                  : 'de la factura'
              }`}
              className={classes.resultTitle}
            />
            <NewPaymentCard
              type={payment.type}
              newTariff={false}
              onChangeNewTariff={() => null}
              details={{
                ...payment.detail
              }}
              scrollOnRender
            />
            {canPayPartially && (
              <SweetAlert
                type={ALERT_TYPE.INFO}
                classes={{ root: classes.sweetAlert }}
                message={
                  <>
                    {contractSingularDemostrative} puede realizar abonos a su
                    deuda actual. Si la deuda cumple con las condiciones para
                    abonar, al continuar al siguiente paso encontrarás la opción
                    de pago <b>Otro valor</b>.
                  </>
                }
              />
            )}
          </div>
        )}
      </Container>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    justifyContent: 'space-around',
    padding: theme.spacing(0),
    marginBottom: 200,
    // * Responsive
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(),
      marginBottom: theme.spacing()
    }
  },
  consultContainer: {
    textAlign: 'end',
    // * Mobile
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      paddingBottom: '0 !important'
    },
    padding: theme.spacing(2, 0)
  },
  inputContainer: {
    // * Mobile
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      marginTop: theme.spacing(),
      paddingTop: '0 !important',
      paddingBottom: '0 !important'
    },
    padding: 0
  },
  recaptchaContainer: {
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      marginBottom: 24
    }
  },
  captchaWeb: {
    // * Mobile
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  resultTitle: {
    marginTop: theme.spacing(3)
  },
  formContainer: {
    marginBottom: theme.spacing(2)
  },
  captchaMobile: {
    // * Mobile
    paddingTop: theme.spacing(5),
    [`@media (max-width:${theme.breakpoints.values.xm - 1}px)`]: {
      paddingTop: theme.spacing(4),
      paddingLeft: theme.spacing(1)
    },
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  },
  captchaLoading: {
    visibility: 'hidden'
  },
  sweetAlert: {
    marginTop: theme.spacing(2)
  },
  paymentReferenceSweetAlert: {
    margin: theme.spacing(1, 0, 2)
  }
}));

export default IndividualPaymentGDC;
